import { InvokeCommand, LambdaClient } from '@aws-sdk/client-lambda';
import { Auth } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { TCustomReportInput, TCustomReportQuery } from '../../generated/graphql';

export type TCustomReportRequest = TCustomReportInput & { breakdownByCategory?: boolean };
export type TCustomReportResult = (TCustomReportQuery['customReport'][number] & { categories?: Record<string, number> })[];

export const queryCustomReport = async (input: TCustomReportRequest, signal: AbortSignal): Promise<TCustomReportResult> => {
  const functionName = import.meta.env.REACT_APP_REPORTS_FUNCTION_NAME;
  if (!functionName) throw new Error('Reports are not configured for this environment.');
  const { credentials } = await Auth.currentSession();
  if (!credentials) throw new Error('Your session is unavailable. Please sign in again.');

  const client = new LambdaClient({
    region: import.meta.env.REACT_APP_REGION,
    credentials,
    maxAttempts: 1,
    // Allow the report's 15-minute execution window and time to return its response.
    requestHandler: { requestTimeout: 930_000 }
  });
  try {
    const response = await client.send(new InvokeCommand({ FunctionName: functionName, InvocationType: 'RequestResponse', Payload: new TextEncoder().encode(JSON.stringify(input)) }), { abortSignal: signal });
    if (!response.Payload?.length) throw new Error('The report returned no result.');
    const result = JSON.parse(new TextDecoder().decode(response.Payload));
    if (response.FunctionError) throw new Error(result?.errorMessage || 'Report generation failed.');
    if (!Array.isArray(result)) throw new Error('The report returned an invalid result.');
    if (input.breakdownByCategory && result.some((row) => !row.categories || typeof row.categories !== 'object' || Array.isArray(row.categories))) throw new Error('Category breakdown is currently unavailable. Please try again later.');
    return result;
  } finally {
    client.destroy();
  }
};
